import { css, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { MapSettings } from './Types'
import { countries } from './Countries'
import { translate } from '../Shared/Translations'

@customElement('lindberg-dealers-search')
export class Search extends LitElement {
	static styles = css`
        :host {
            display: block;
			font-family: var(--font-family);
        }
		form{
			display: flex;
			gap: var(--space-200);
			font-size:var(--font-size-base-md);
			align-items: center;
		}
		form div {
			border-radius: calc(var(--font-size-base-md) * 1.5 / 2 + var(--space-100));
			border: 1px solid #d9c8ab;
			background: #f7f5f2;
			overflow: hidden;
		}
		input:focus {
			background: white;
		}
		input, ul {
			font-family: inherit;
			box-sizing: border-box;
			width: 320px;
			border:none;
			background: transparent;
			outline: none;
			font-size: var(--font-size-base-md);
			padding: var(--space-100) var(--space-200);			
			line-height: 1.5;
		}
		ul {
			list-style: none;
			padding: 0;
			margin: 0;
		}
		li {
			padding: 8px;
			border-top: 1px solid #d9c8ab;
			
			&:hover, &[aria-selected="true"] {
				background-color: white;
			}


			h2 {
				font-weight: inherit;
				font-size: 18px;
				margin: 0;
			}
			span {
				font-size: 13px;
			}
		}
    `

	@query('input')
	search!: HTMLInputElement

	@state()
	options: FeatureCollection | null = null

	@state()
	activeIndex = -1

	@property({ attribute: false })
	settings!: MapSettings

	connectedCallback(): void {
		super.connectedCallback()
		const urlParams = new URLSearchParams(window.location.search)
		const filtersParam = urlParams.get('filter')
		if (filtersParam) {
			// this.settings.activeFilters = filtersParam.split(',').map(f => f.trim())
		}
	}

	async geoCode() {
		if (this.search.value.length > 2) {
			this.options = await (await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${
				new URLSearchParams({
					q: this.search.value,
					language: this.settings.language,
					worldview: this.settings.worldView,
					proximity: 'ip',
					access_token: import.meta.env.VITE_MAPBOX_TOKEN
				}).toString()
			}`)).json()
			this.activeIndex = -1

			console.log(this.options)
		}
	}

	onKeyDown(e: KeyboardEvent) {
		const features = this.options?.features
		if (!features?.length) return

		if (e.key === 'ArrowDown') {
			e.preventDefault()
			this.activeIndex = (this.activeIndex + 1) % features.length
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			this.activeIndex = (this.activeIndex - 1 + features.length) % features.length
		} else if (e.key === 'Enter') {
			if (this.activeIndex >= 0) {
				e.preventDefault()
				this.selectLocation(features[this.activeIndex])
			}
		} else if (e.key === 'Escape') {
			this.options = null
			this.activeIndex = -1
		}
	}

	selectLocation(location: Feature) {
		console.log(location)

		// const bounds = location.properties.bbox ? { north: location.properties.bbox[3], south: location.properties.bbox[1], west: location.properties.bbox[0], east: location.properties.bbox[2] } : null
		const coordinates = location.geometry.coordinates
		if (location.properties.feature_type == 'country') {
			const country = countries.find(c => c.code == location.properties.context.country?.country_code?.toUpperCase())
			console.log('country selected', country)
			if(country) this.dispatchEvent(new CustomEvent('country-selected', { bubbles: true, detail: { country} }))
			// this.dispatchEvent(new CustomEvent('location-selected', { detail: { country: location.properties.context.country.country_code, bounds: location.properties.bbox, coordinates } }))
		} else if(location.properties.context.country?.country_code){
			console.log('location selected', location.properties.context.country.country_code, location.properties.bbox, coordinates)
			const country = countries.find(c => c.code == location.properties.context.country?.country_code?.toUpperCase())
			console.log('country selected', country)

			this.dispatchEvent(new CustomEvent('location-selected', { bubbles: true, detail: { country, bounds: location.properties.bbox, coordinates } }))
		}
		this.search.value = location.properties.name_preferred
		this.options = null
		this.activeIndex = -1
	}

	removeFilter(x: string) {
		if(!this.settings.activeFilters)return 
		this.settings.activeFilters.splice(this.settings.activeFilters.indexOf(x), 1)
		this.requestUpdate()
		this.dispatchEvent(new CustomEvent('filters-changed', { detail: x }))
	}

	render() {
		return html`
		<form @submit=${(e: Event) => e.preventDefault()}>
            <div>
                <input type="text" @input=${this.geoCode} @keydown=${this.onKeyDown} aria-label=${translate('search-label', {}, this.settings.language)} placeholder=${translate('search-placeholder', {}, this.settings.language)} />
                <ul>
                    ${this.options ? this.options.features.map((feature, i) => html`<li aria-selected=${i === this.activeIndex} @click=${() => this.selectLocation(feature)}><h2>${feature.properties.name_preferred}</h2><span>${feature.properties.place_formatted}</span></li>`) : nothing}
                </ul>
            </div>
			${this.settings.activeFilters?.map(x => html`<lindberg-button small solid @click="${() => this.removeFilter(x)}">${x}</lindberg-button>`)}
		</form>`
		// <!-- ${this.filters.map(x => html`<label><input type="checkbox" ?checked=${this.settings.activeFilters.includes(x)} @change=${e => this.toggleFilter(x, e.currentTarget.checked)} /> ${x}</label>`)} -->
	}

	toggleFilter(x: string, enabled: boolean) {
		if(!this.settings.activeFilters)return 
		if (enabled) this.settings.activeFilters.push(x)
		else this.settings.activeFilters.splice(this.settings.activeFilters.indexOf(x), 1)

		this.dispatchEvent(new CustomEvent('filters-changed', { detail: x }))
	}
}
type FeatureType = 'place' | 'locality' | 'street' | 'country'

type Geometry = {
	type: 'Point'
	coordinates: [number, number]
}

type Coordinates = {
	longitude: number
	latitude: number
}

type BBox = [number, number, number, number]

type ContextEntry = {
	mapbox_id: string
	name: string
	wikidata_id?: string
	region_code?: string
	region_code_full?: string
	country_code?: string
	country_code_alpha_3?: string
}

type Context = {
	region?: ContextEntry
	country?: ContextEntry
	place?: ContextEntry
	locality?: ContextEntry
	street?: ContextEntry
	postcode?: ContextEntry
}

type Properties = {
	mapbox_id: string
	feature_type: FeatureType
	full_address: string
	name: string
	name_preferred: string
	coordinates: Coordinates
	place_formatted: string
	bbox?: BBox
	context: Context
}

type Feature = {
	type: 'Feature'
	id: string
	geometry: Geometry
	properties: Properties
}

type FeatureCollection = {
	type: 'FeatureCollection'
	features: Feature[]
	attribution: string
}

declare global {
	interface HTMLElementTagNameMap {
		'lindberg-dealers-search': Search
	}
}
